import {
  Job,
  Organization,
  OrganizationLinkedServiceName,
  SaveQuickbooksIntegrationInput,
} from "generated/graphql";
import { useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import useGlobalMessage from "hooks/notifications/useGlobalMessage";
import useGetOrganization from "hooks/organization/useGetOrganization";
import { useGetJob } from "hooks/jobs/useGetJob";
import useGetQuickbooksSyncJobs from "hooks/jobs/useGetQuickbooksSyncJobs";
import useJobsBillingStatuses from "hooks/jobs/useJobsBillingStatuses";
import useSyncQuickbooksBatch, {
  SyncQuickbooksBatchInput,
  SyncQuickbooksBatchLineInput,
  SyncQuickbooksBatchManualLineInput,
} from "hooks/jobs/useSyncQuickbooksBatch";
import {
  allocateAmounts,
  asNumber,
  BillingMode,
  createAggregateFallbackLine,
  createFallbackLinesFromFormData,
  DEFAULT_BILLING_STATUSES,
  EditableLine,
  JobBillingStatusValue,
  mergeEditableLines,
  normalizeBillingStatusValue,
  normalizeSyncableJob,
  parseAmountInputToCents,
  QuickbooksFormItem,
  SyncableJob,
  toJobBillingStatusEnum,
  toQuickbooksBillingModeEnum,
} from "modules/jobs/quickbooksSyncUtils";

type QuickbooksSyncDraftProps = {
  externalId: string;
  job: Job;
  mayShowTaxNotification?: Boolean;
  onClose?: () => void;
  shouldPushTaxesToQuickbooks?: Boolean;
};

export default function useQuickbooksSyncBatchDraft({
  externalId,
  job,
  mayShowTaxNotification,
  onClose,
  shouldPushTaxesToQuickbooks,
}: QuickbooksSyncDraftProps) {
  const seedJobId = String(job.id);
  const { data } = useGetOrganization();
  const { show } = useGlobalMessage();
  const [formData, setFormData] = useState<SaveQuickbooksIntegrationInput | null>(null);
  const [syncInvoice, setSyncInvoice] = useState(true);
  const [syncEstimate, setSyncEstimate] = useState(false);
  const [syncJob, setSyncJob] = useState(false);
  const [billingMode, setBillingModeState] = useState<BillingMode>("full");
  const [jobBillingStatus, setJobBillingStatus] = useState<JobBillingStatusValue>("billed");
  const [percentageValue, setPercentageValueState] = useState("50");
  const [fixedAmountValue, setFixedAmountValueState] = useState("");
  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([seedJobId]);
  const [editableLines, setEditableLines] = useState<EditableLine[]>([]);
  const [jobPickerOpen, setJobPickerOpen] = useState(false);
  const [jobPickerSearch, setJobPickerSearch] = useState("");
  const [pickerSelectedJobIds, setPickerSelectedJobIds] = useState<string[]>([]);
  const [syncQuickbooksBatch, { loading }] = useSyncQuickbooksBatch();

  const hasLinkedQuickbooksDesktop = !!data?.linkedServices?.find(
    (service) =>
      service.service === OrganizationLinkedServiceName.QUICKBOOKS_DESKTOP && service.active
  );

  const hasLinkedQuickbooksOnline = !!data?.linkedServices?.find(
    (service) => service.service === OrganizationLinkedServiceName.QUICKBOOKS && service.active
  );

  const { data: companyJobsData, loading: jobsLoading } = useGetQuickbooksSyncJobs({
    variables: {
      companyId: job.company.id,
      search: jobPickerSearch || undefined,
      first: 100,
      page: 1,
    },
  });
  const { data: currentJobData } = useGetJob({
    variables: {
      id: seedJobId,
    },
  });

  const { data: selectedJobsData } = useGetQuickbooksSyncJobs({
    variables: {
      jobIds: selectedJobIds.filter((id) => id !== seedJobId),
      first: Math.max(selectedJobIds.length - 1, 1),
      page: 1,
    },
  });
  const { data: billingStatuses } = useJobsBillingStatuses();
  const resolvedSeedJob = currentJobData ?? job;
  const availableBillingStatuses = useMemo<JobBillingStatusValue[]>(() => {
    const normalizedStatuses = (
      billingStatuses?.length
        ? billingStatuses.map((status) => normalizeBillingStatusValue(status))
        : DEFAULT_BILLING_STATUSES
    ).filter((status, index, all) => all.indexOf(status) === index);

    return normalizedStatuses.includes("billed")
      ? normalizedStatuses
      : ["billed", ...normalizedStatuses];
  }, [billingStatuses]);

  const availableJobs = useMemo(() => {
    const jobs = (companyJobsData ?? []).map((queryJob) => normalizeSyncableJob(queryJob));
    const seed = normalizeSyncableJob(resolvedSeedJob);
    const map = new Map<string, SyncableJob>(jobs.map((entry) => [entry.id, entry]));
    const existingSeed = map.get(seed.id);
    const preferredSeed =
      existingSeed && existingSeed.phases.length > seed.phases.length ? existingSeed : seed;
    map.set(seed.id, preferredSeed);

    return Array.from(map.values()).sort((a, b) => Number(a.id) - Number(b.id));
  }, [companyJobsData, resolvedSeedJob]);

  const selectedJobs = useMemo(() => {
    const queriedJobs = (selectedJobsData ?? []).map((queryJob) => normalizeSyncableJob(queryJob));
    const map = new Map<string, SyncableJob>(queriedJobs.map((entry) => [entry.id, entry]));
    const seed = normalizeSyncableJob(resolvedSeedJob);
    map.set(seed.id, seed);

    return selectedJobIds
      .map((jobId) => map.get(jobId))
      .filter((entry): entry is SyncableJob => Boolean(entry));
  }, [resolvedSeedJob, selectedJobIds, selectedJobsData]);

  const pickerJobs = useMemo(() => {
    const selectedSet = new Set(selectedJobIds);
    return availableJobs.filter((candidate) => !selectedSet.has(candidate.id));
  }, [availableJobs, selectedJobIds]);

  useEffect(() => {
    setEditableLines((current) => mergeEditableLines(current, selectedJobs));
  }, [selectedJobs]);

  useEffect(() => {
    const hasGeneratedSelectedLines = selectedJobs.some(
      (selectedJob) => selectedJob.phases.length > 0
    );
    const formItems = (formData?.items as QuickbooksFormItem[] | undefined) ?? [];
    const seedJobName = resolvedSeedJob.proposalStage?.name || `Job ${resolvedSeedJob.externalId}`;
    const formFallbackLines =
      formItems.length > 0
        ? createFallbackLinesFromFormData({
            items: formItems,
            seedJobId,
            seedJobExternalId: resolvedSeedJob.externalId,
            seedJobName,
          })
        : [];
    const aggregateFallbackLines =
      formFallbackLines.length === 0
        ? createAggregateFallbackLine({
            seedJobId,
            seedJobExternalId: resolvedSeedJob.externalId,
            seedJobName,
            amountCents: asNumber(resolvedSeedJob.proposedOverallTotal),
          })
        : [];

    if (hasGeneratedSelectedLines) {
      return;
    }

    setEditableLines((current) => {
      const hasExistingVisibleLines = current.some((line) => !line.removed);
      if (hasExistingVisibleLines) {
        return current;
      }

      return formFallbackLines.length > 0 ? formFallbackLines : aggregateFallbackLines;
    });
  }, [formData, resolvedSeedJob, seedJobId, selectedJobs]);

  useEffect(() => {
    setJobBillingStatus("billed");
  }, [seedJobId, externalId]);

  const visibleLines = useMemo(
    () => editableLines.filter((line) => !line.removed),
    [editableLines]
  );
  const syncAmountOverrideCount = useMemo(
    () => visibleLines.filter((line) => line.hasSyncAmountOverride).length,
    [visibleLines]
  );

  const allocationPreview = useMemo(
    () =>
      allocateAmounts(
        visibleLines.map((line) => ({
          key: line.id,
          amount: parseAmountInputToCents(line.amountInput),
          overrideAmount: line.hasSyncAmountOverride
            ? parseAmountInputToCents(line.syncAmountInput || "")
            : undefined,
        })),
        billingMode,
        percentageValue,
        fixedAmountValue
      ),
    [billingMode, fixedAmountValue, percentageValue, visibleLines]
  );

  const resetSyncAmountOverrides = () => {
    setEditableLines((current) =>
      current.map((line) => ({
        ...line,
        hasSyncAmountOverride: false,
        syncAmountInput: undefined,
      }))
    );
  };

  const setBillingMode = (value: BillingMode) => {
    setBillingModeState(value);
    resetSyncAmountOverrides();
  };

  const setPercentageValue = (value: string) => {
    setPercentageValueState(value);
    resetSyncAmountOverrides();
  };

  const setFixedAmountValue = (value: string) => {
    setFixedAmountValueState(value);
    resetSyncAmountOverrides();
  };

  const updateLine = (
    lineId: string,
    field: "itemName" | "itemDescription" | "amountInput",
    value: string
  ) => {
    setEditableLines((current) =>
      current.map((line) => (line.id === lineId ? { ...line, [field]: value } : line))
    );
  };

  const updateSyncAmount = (lineId: string, value: string) => {
    setEditableLines((current) =>
      current.map((line) =>
        line.id === lineId ? { ...line, hasSyncAmountOverride: true, syncAmountInput: value } : line
      )
    );
  };

  const removeLine = (lineId: string) => {
    setEditableLines((current) =>
      current.flatMap((line) => {
        if (line.id !== lineId) {
          return [line];
        }

        if (line.sourceType === "manual") {
          return [];
        }

        return [{ ...line, removed: true }];
      })
    );
  };

  const addManualLine = () => {
    const id = `manual:${uuidv4()}`;
    setEditableLines((current) => [
      ...current,
      {
        id,
        sourceType: "manual",
        sourceKey: id,
        itemName: "",
        itemDescription: "",
        amountInput: "",
        removed: false,
      },
    ]);
  };

  const removeSelectedJob = (jobId: string) => {
    if (jobId === seedJobId) {
      return;
    }

    setSelectedJobIds((current) => current.filter((id) => id !== jobId));
  };

  const togglePickerJob = (jobId: string) => {
    setPickerSelectedJobIds((current) =>
      current.includes(jobId) ? current.filter((id) => id !== jobId) : [...current, jobId]
    );
  };

  const addJobsToBatch = () => {
    if (pickerSelectedJobIds.length === 0) {
      setJobPickerOpen(false);
      return;
    }

    setSelectedJobIds((current) => Array.from(new Set([...current, ...pickerSelectedJobIds])));
    setPickerSelectedJobIds([]);
    setJobPickerSearch("");
    setJobPickerOpen(false);
  };

  const closeJobPicker = () => {
    setJobPickerOpen(false);
    setPickerSelectedJobIds([]);
    setJobPickerSearch("");
  };

  const getSyncInput = (): SyncQuickbooksBatchInput | undefined => {
    if (!formData) {
      show({
        message: "QuickBooks details are still loading. Please review the form again.",
        options: { variant: "error", autoHideDuration: 10000 },
      });
      return undefined;
    }

    const lineInputs: SyncQuickbooksBatchLineInput[] = [];
    const manualLineInputs: SyncQuickbooksBatchManualLineInput[] = [];

    visibleLines.forEach((line, index) => {
      const amount = parseAmountInputToCents(line.amountInput);
      const billedAmount = line.hasSyncAmountOverride
        ? parseAmountInputToCents(line.syncAmountInput || "")
        : undefined;

      if (line.sourceType === "manual") {
        manualLineInputs.push({
          itemName: line.itemName.trim(),
          itemDescription: line.itemDescription.trim() || undefined,
          amount,
          billedAmount,
          sortOrder: index,
        });
        return;
      }

      lineInputs.push({
        jobId: line.jobId || seedJobId,
        jobPhaseId: line.jobPhaseId,
        proposalProductGroupId: line.proposalProductGroupId ?? undefined,
        groupName: line.groupName ?? undefined,
        itemName: line.itemName.trim(),
        itemDescription: line.itemDescription.trim() || undefined,
        amount,
        billedAmount,
        sortOrder: index,
      });
    });

    return {
      jobIds: selectedJobs.map((entry) => entry.id),
      pushOnline: hasLinkedQuickbooksOnline,
      pushDesktop: hasLinkedQuickbooksDesktop,
      createInvoice: syncInvoice,
      createEstimate: syncEstimate,
      createJob: syncJob,
      billingMode: toQuickbooksBillingModeEnum(billingMode),
      partialPercentage: billingMode === "percentage" ? Number(percentageValue) : undefined,
      partialAmountCents:
        billingMode === "fixed_amount" ? parseAmountInputToCents(fixedAmountValue) : undefined,
      billingStatus: toJobBillingStatusEnum(jobBillingStatus),
      customerName: formData.customerName,
      customerPhone: formData.customerPhone,
      customerEmail: formData.customerEmail,
      customerBillingAddressLine1: formData.customerBillingAddressLine1,
      customerBillingAddressLine2: formData.customerBillingAddressLine2,
      customerBillingAddressZip: formData.customerBillingAddressZip,
      customerBillingAddressCity: formData.customerBillingAddressCity,
      customerBillingAddressState: formData.customerBillingAddressState,
      customerBillingAddressCountry: formData.customerBillingAddressCountry,
      invoiceNumber: formData.invoiceNumber,
      taxReference: formData.taxReference,
      jobName: formData.jobName,
      shipAddressAddressLine1: formData.shipAddressAddressLine1,
      shipAddressAddressLine2: formData.shipAddressAddressLine2,
      shipAddressAddressZip: formData.shipAddressAddressZip,
      shipAddressAddressCity: formData.shipAddressAddressCity,
      shipAddressAddressState: formData.shipAddressAddressState,
      lines: lineInputs,
      manualLines: manualLineInputs,
    };
  };

  const submitSyncInput = async (input: SyncQuickbooksBatchInput) => {
    try {
      const response = await syncQuickbooksBatch({
        variables: { input },
      });

      if (mayShowTaxNotification && !shouldPushTaxesToQuickbooks) {
        show({
          message:
            "Taxes will not be added to the QuickBooks invoice because more than one tax rate is associated to the proposal or none at all. Please apply taxes to the invoice within QuickBooks.",
          options: { variant: "info", autoHideDuration: 10000 },
        });
      }

      const batch = response.data?.syncQuickbooksBatch;
      if (batch) {
        show({
          message: hasLinkedQuickbooksDesktop
            ? "QuickBooks sync submitted. Online was processed immediately and Desktop was queued."
            : "QuickBooks sync submitted.",
          options: { variant: "success", autoHideDuration: 10000 },
        });
        onClose?.();
      }
    } catch (error) {
      show({
        message: "There was an error syncing with QuickBooks, please try again.",
        options: { variant: "error", autoHideDuration: 10000 },
      });
    }
  };

  return {
    allocationPreview,
    availableBillingStatuses,
    billingMode,
    closeJobPicker,
    editableLines,
    fixedAmountValue,
    formData,
    getSyncInput,
    jobBillingStatus,
    jobPickerOpen,
    jobPickerSearch,
    jobsLoading,
    loading,
    percentageValue,
    pickerJobs,
    pickerSelectedJobIds,
    removeLine,
    removeSelectedJob,
    resetSyncAmountOverrides,
    resolvedSeedJob,
    seedJobId,
    selectedJobIds,
    selectedJobs,
    syncAmountOverrideCount,
    setBillingMode,
    setFixedAmountValue,
    setFormData,
    setJobBillingStatus,
    setJobPickerOpen,
    setJobPickerSearch,
    setPercentageValue,
    setSyncEstimate,
    setSyncInvoice,
    setSyncJob,
    submitSyncInput,
    syncEstimate,
    syncInvoice,
    syncJob,
    togglePickerJob,
    updateLine,
    updateSyncAmount,
    visibleLines,
    addJobsToBatch,
    addManualLine,
  } as const;
}
